// pages/secure/OfficeRegistryInternalPage.js
// The registry the secure portal must contain under 68 IAC 1450.610(c)(3)(D)(ii):
// sponsored licensees, their designated managing brokers, and identification of any
// additional office locations registered with the Division.
//
// Every figure is derived from live records, so the registry cannot drift out of date.
// Printable, because it is the thing most likely to be asked for during an inspection.
import React, { useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const StatusPill = ({ status }) => {
    const tone = status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600';
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${tone}`}>{status}</span>;
};

const Card = ({ title, subtitle, children }) => (
    <section className="bg-white rounded-lg border border-slate-200 mb-5">
        <div className="px-4 py-3 border-b bg-slate-50">
            <h2 className="font-semibold text-slate-800">{title}</h2>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="p-4">{children}</div>
    </section>
);

const OfficeRegistryInternalPage = () => {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get('/api/registry').then(setData).catch(e => setError(e.message));
    }, [api]);

    if (error) return <div className="p-6"><p className="text-red-600">{error}</p></div>;
    if (!data) return <div className="p-6 text-slate-500">Loading registry…</div>;

    const { brokerage, offices, managing_brokers, licensees, employees } = data;
    const sop = offices.address;

    return (
        <div className="p-6 max-w-5xl">
            <style>{`@media print { .no-print { display:none } }`}</style>

            <div className="flex items-start justify-between mb-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Office Registry</h1>
                    <p className="text-sm text-gray-500">
                        Sponsored licensees, designated managing brokers, and office locations —
                        68 Ill. Adm. Code 1450.610(c)(3)(D)(ii).
                    </p>
                </div>
                <button onClick={() => window.print()}
                    className="no-print text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50">🖨 Print</button>
            </div>

            <Card title={brokerage.name} subtitle="Sponsoring broker">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Sponsoring broker license</span><br /><b>{brokerage.sponsoring_broker_license_number || '—'}</b></div>
                    <div><span className="text-slate-500">Virtual office license</span><br />
                        <b>{brokerage.virtual_office_license_number || <span className="text-amber-700">Registration pending with IDFPR</span>}</b></div>
                    <div className="sm:col-span-2"><span className="text-slate-500">Service area</span><br /><b>{brokerage.service_area || '—'}</b></div>
                </div>
            </Card>

            <Card title="Office location">
                <div className="text-sm space-y-3">
                    <address className="not-italic text-slate-700">
                        {sop.street}<br />{sop.city}, {sop.state} {sop.zip}
                    </address>
                    <a href={offices.virtual_office_url} className="text-indigo-600 hover:underline block">
                        {offices.virtual_office_url}
                    </a>
                </div>
            </Card>

            <Card title="Designated managing broker" subtitle="Supervision under 225 ILCS 454/10-20 and 68 IAC 1450.705">
                <DataList rows={managing_brokers} rowKey={(m) => `${m.source}-${m.id}`} csvName="managing-brokers" defaultSort={{ key: 'name', dir: 'asc' }}
                    empty={<span className="text-amber-700">No active designated managing broker on record.</span>}
                    columns={[
                        { key: 'name', label: 'Name', render: (m) => <span className="font-medium">{m.name}</span> },
                        { key: 'license_number', label: 'License', render: (m) => m.license_number || '—' },
                        { key: 'license_expiration_date', label: 'Expires', type: 'date', render: (m) => fmtDate(m.license_expiration_date) },
                        { key: 'phone', label: 'Phone', render: (m) => m.phone || '—' },
                        { key: 'email', label: 'Email', render: (m) => m.email || '—' },
                    ]} />
            </Card>

            <Card title={`Sponsored licensees (${data.active_licensee_count} active)`}
                  subtitle="Each licensee and the designated managing broker who supervises them">
                <div className="overflow-x-auto">
                    <DataList rows={licensees} rowKey="agent_id" csvName="sponsored-licensees" defaultSort={{ key: 'name', dir: 'asc' }} empty="No sponsored licensees on record."
                        filters={[{ key: 'status', label: 'Status' }, { key: 'supervising_dmb', label: 'Supervising broker' }]}
                        columns={[
                            { key: 'name', label: 'Name', value: (l) => `${l.name}${l.is_managing_broker ? ' (managing broker)' : ''}`, sortValue: (l) => l.name,
                                render: (l) => <span className="font-medium">{l.name}{l.is_managing_broker && <span className="ml-2 text-xs bg-slate-100 px-2 py-0.5 rounded">Managing broker</span>}</span> },
                            { key: 'license_number', label: 'License', render: (l) => l.license_number || '—' },
                            { key: 'license_expiration_date', label: 'Expires', type: 'date', render: (l) => fmtDate(l.license_expiration_date) },
                            { key: 'status', label: 'Status', render: (l) => <StatusPill status={l.status} /> },
                            { key: 'supervising_dmb', label: 'Supervising managing broker', value: (l) => (l.supervising_dmb ? `${l.supervising_dmb}${l.supervising_dmb_license ? ` · ${l.supervising_dmb_license}` : ''}` : 'Unassigned'),
                                sortValue: (l) => l.supervising_dmb || null,
                                render: (l) => <>{l.supervising_dmb || <span className="text-amber-700">Unassigned</span>}{l.supervising_dmb_license && <span className="text-slate-400"> · {l.supervising_dmb_license}</span>}</> },
                        ]} />
                </div>
            </Card>

            <Card title="Office staff" subtitle="Employees and unlicensed assistants">
                <div className="overflow-x-auto">
                    <DataList rows={employees} rowKey="employee_id" csvName="office-staff" defaultSort={{ key: 'name', dir: 'asc' }} empty="No office staff on record."
                        filters={[{ key: 'status', label: 'Status' }, { key: 'staff_role', label: 'Access level' }]}
                        columns={[
                            { key: 'name', label: 'Name', value: (e) => `${e.name}${e.is_designated_manager ? ' (DMB)' : ''}`, sortValue: (e) => e.name,
                                render: (e) => <span className="font-medium">{e.name}{e.is_designated_manager && <span className="ml-2 text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded">DMB</span>}</span> },
                            { key: 'job_title', label: 'Title', render: (e) => e.job_title || '—' },
                            { key: 'staff_role', label: 'Access level', render: (e) => e.staff_role || '—' },
                            { key: 'license_number', label: 'License', value: (e) => e.license_number || 'unlicensed', render: (e) => e.license_number || <span className="text-slate-400">unlicensed</span> },
                            { key: 'status', label: 'Status', render: (e) => <StatusPill status={e.status} /> },
                        ]} />
                </div>
            </Card>

            <p className="text-xs text-slate-400">
                Generated live from brokerage records {new Date(data.generated_at).toLocaleString()}.
            </p>
        </div>
    );
};

export default OfficeRegistryInternalPage;
